import { z } from 'zod';

// Bodies of the document write routes of `apps/backend/routes/api.routes.ts`.
// The backend turns each accepted key into a `case_documents` column name, so
// these schemas are `.strict()`: a key outside the list is refused with a 400
// instead of reaching the SQL text (issue #1882). The lists are the union of
// what legalworkspace, the Word add-in and addin-kit send today — widen one only
// together with the client that needs the new field.

const folderPath = z.array(z.string());
// Clients send the piece number as typed text, except the merge flow which
// sends the computed rank as a number.
const pieceNumber = z.union([z.string(), z.number()]).nullable();

const placementFields = {
  document_category: z.string(),
  document_subcategory: z.string().nullable(),
  folder_path: folderPath,
  parent_folder_path: folderPath,
  display_folder_path: z.string().nullable(),
  folder_depth: z.number().int().nonnegative(),
};

/** Body of `POST /api/cases/:caseId/documents`. */
export const CreateCaseDocumentRequestSchema = z
  .object({
    id: z.string().uuid(),
    // Sent by the Word add-in research flow; the route always overwrites it
    // with the `:caseId` of the URL, on which the access check ran.
    case_id: z.string().uuid(),
    name: z.string(),
    file_path: z.string(),
    document_type: z.string().nullable(),
    ...placementFields,
    is_folder: z.boolean(),
    file_size: z.number().nonnegative().nullable(),
    piece_number: pieceNumber,
    piece_description: z.string().nullable(),
    sender_email: z.string().nullable(),
    sender_name: z.string().nullable(),
  })
  .partial()
  .strict();
export type CreateCaseDocumentRequest = z.infer<typeof CreateCaseDocumentRequestSchema>;

/** Body of `POST /api/cases/:caseId/documents/folder`. */
export const CreateCaseFolderRequestSchema = z
  .object({
    name: z.string(),
    // Marks a system folder (`SYSTEM_FOLDER_DOCUMENT_TYPE`); null for a lawyer's folder.
    document_type: z.string().nullable(),
    ...placementFields,
  })
  .partial()
  .strict();
export type CreateCaseFolderRequest = z.infer<typeof CreateCaseFolderRequestSchema>;

/** Body of `PATCH /api/documents/:documentId` (rename, renumber, describe, move). */
export const UpdateDocumentRequestSchema = z
  .object({
    name: z.string(),
    piece_number: pieceNumber,
    piece_description: z.string().nullable(),
    ...placementFields,
  })
  .partial()
  .strict();
export type UpdateDocumentRequest = z.infer<typeof UpdateDocumentRequestSchema>;

/** Body of `PATCH /api/documents/batch/move`. */
export const MoveDocumentsRequestSchema = z
  .object({
    documents: z.array(
      z
        .object(placementFields)
        .partial()
        .extend({ id: z.string().min(1) })
        .strict(),
    ),
  })
  .strict();
export type MoveDocumentsRequest = z.infer<typeof MoveDocumentsRequestSchema>;
